import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PawPrint, Receipt } from 'lucide-react'
import { deleteClient, getClient, sendPortalInvite } from '../../api/clients'
import { listInvoices } from '../../api/invoices'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { useConfirm } from '../../components/ui/ConfirmModal'
import { useToast } from '../../components/ui/Toast'
import { useFetch } from '../../hooks/useFetch'
import {
  crumbLinkClass, infoLabelClass, infoValueClass,
  tbodyHoverClass, tdClass, thClass,
} from '../../lib/tableStyles'
import type { PatientListItem } from '../../types/patients'
import { SEX_LABELS, SPECIES_LABELS } from '../../types/patients'
import type { InvoiceListItem, InvoiceStatus } from '../../types/invoices'
import { STATUS_COLORS, STATUS_LABELS as INVOICE_STATUS_LABELS } from '../../types/invoices'

const desexedLabel = (val: boolean | null) =>
  val === true ? 'Yes' : val === false ? 'No' : 'Unknown'

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [archiving, setArchiving] = useState(false)
  const [inviting, setInviting] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  const { data: client, loading, error } = useFetch(
    () => getClient(Number(id)),
    [id],
  )

  const { data: invoicesData } = useFetch(
    () => (id ? listInvoices({ client_id: Number(id), limit: 50 }) : Promise.resolve([])),
    [id],
  )

  const patients: PatientListItem[] = client?.patients ?? []
  const invoices: InvoiceListItem[] = invoicesData ?? []

  const handlePortalInvite = async () => {
    if (!client) return
    const ok = await confirm({
      title: client.has_portal_access ? 'Resend portal invite?' : 'Invite to client portal?',
      message: `A portal access link will be emailed to ${client.email}.`,
      confirmLabel: 'Send Invite',
    })
    if (!ok) return
    setInviting(true)
    try {
      await sendPortalInvite(client.id)
      toast.success(`Portal invite sent to ${client.email}.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send invite.')
    } finally {
      setInviting(false)
    }
  }

  const handleArchive = async () => {
    if (!client) return
    const ok = await confirm({
      title: 'Archive client?',
      message: `${client.first_name} ${client.last_name} will be archived and won't appear in active lists.`,
      confirmLabel: 'Archive',
    })
    if (!ok) return
    setArchiving(true)
    try {
      await deleteClient(client.id)
      toast.success(`${client.first_name} ${client.last_name} has been archived.`)
      void navigate('/clients')
    } catch {
      toast.error('Failed to archive client. Please try again.')
      setArchiving(false)
    }
  }

  if (loading) return <div className="text-text-2 py-8">Loading…</div>

  if (error || !client) {
    return (
      <div>
        <ErrorBox>{error ?? 'Client not found.'}</ErrorBox>
        <Link to="/clients" className={crumbLinkClass}>← Back to clients</Link>
      </div>
    )
  }

  const addressParts = [
    client.address_line1,
    client.address_line2,
    [client.city, client.state].filter(Boolean).join(', '),
    client.postal_code,
    client.country,
  ].filter(Boolean)

  return (
    <div>
      <nav className="text-[0.85rem] text-text-3 mb-5 flex items-center gap-[0.4rem]">
        <Link to="/clients" className={crumbLinkClass}>Clients</Link>
        <span>›</span>
        <span>{client.first_name} {client.last_name}</span>
      </nav>

      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">
            {client.first_name} {client.last_name}
          </h1>
          <div className="mt-[0.2rem] flex items-center gap-2 flex-wrap">
            <p className="m-0 text-[0.9rem] text-text-3">
              Client since {new Date(client.created_at).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            {client.has_portal_access && (
              <span className="inline-flex items-center px-2 py-[0.15rem] rounded-full text-[0.78rem] font-semibold bg-success-bg text-success-text">
                Portal Active
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <Button variant="outline" disabled={inviting} onClick={() => void handlePortalInvite()}>
            {inviting ? 'Sending…' : client.has_portal_access ? 'Resend Portal Invite' : 'Invite to Portal'}
          </Button>
          <ButtonLink to={`/clients/${client.id}/edit`}>Edit</ButtonLink>
          <Button variant="danger" disabled={archiving} onClick={() => void handleArchive()}>
            {archiving ? 'Archiving…' : 'Archive'}
          </Button>
        </div>
      </div>

      <div className="grid gap-5">
        {/* Contact card */}
        <Card>
          <SectionTitle>Contact Information</SectionTitle>
          <div className="grid grid-cols-2 gap-y-[0.85rem] gap-x-8 max-sm:grid-cols-1">
            <div>
              <div className={infoLabelClass}>Email</div>
              <div className={infoValueClass}>{client.email}</div>
            </div>
            <div>
              <div className={infoLabelClass}>Phone</div>
              <div className={infoValueClass}>{client.phone}</div>
            </div>
            {client.phone_alt && (
              <div>
                <div className={infoLabelClass}>Alternate Phone</div>
                <div className={infoValueClass}>{client.phone_alt}</div>
              </div>
            )}
            {addressParts.length > 0 && (
              <div className="col-span-2 max-sm:col-span-1">
                <div className={infoLabelClass}>Address</div>
                <div className={infoValueClass}>
                  {addressParts.map((line, i) => <div key={i}>{line}</div>)}
                </div>
              </div>
            )}
            {client.notes && (
              <div className="col-span-2 max-sm:col-span-1">
                <div className={infoLabelClass}>Notes</div>
                <div className="text-[0.925rem] whitespace-pre-wrap text-text-2">{client.notes}</div>
              </div>
            )}
          </div>
        </Card>

        {/* Patients card */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between p-5 pb-0">
            <SectionTitle style={{ marginBottom: patients.length > 0 ? undefined : 0 }}>Patients ({patients.length})</SectionTitle>
            <ButtonLink to={`/clients/${client.id}/patients/new`} size="sm">+ Add Patient</ButtonLink>
          </div>

          {patients.length === 0 ? (
            <div className="text-center py-10 px-4 text-text-3 p-5">
              <PawPrint size={36} strokeWidth={1.25} className="text-text-4 mb-2 mx-auto" />
              <div className="font-semibold text-text-heading mb-[0.35rem]">No patients yet</div>
              <div className="text-sm mb-4">Add the first patient for this client</div>
              <ButtonLink to={`/clients/${client.id}/patients/new`}>+ Add Patient</ButtonLink>
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className={thClass}>Name</th>
                    <th className={thClass}>Species</th>
                    <th className={thClass}>Breed</th>
                    <th className={thClass}>Sex</th>
                    <th className={thClass}>Desexed</th>
                    <th className={thClass}>Weight</th>
                    <th className={`${thClass} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={tbodyHoverClass}>
                  {patients.map((p) => (
                    <tr key={p.id}>
                      <td className={`${tdClass} font-semibold`}>
                        <Link to={`/patients/${p.id}`} className={crumbLinkClass}>
                          {p.name}
                        </Link>
                      </td>
                      <td className={tdClass}>{SPECIES_LABELS[p.species]}</td>
                      <td className={`${tdClass} text-text-2`}>
                        {p.breed ?? <span className="text-text-4">—</span>}
                      </td>
                      <td className={tdClass}>{SEX_LABELS[p.sex]}</td>
                      <td className={tdClass}>{desexedLabel(p.is_desexed)}</td>
                      <td className={tdClass}>
                        {p.weight_kg != null ? `${p.weight_kg} kg` : <span className="text-text-4">—</span>}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        <Link to={`/clients/${client.id}/patients/${p.id}/edit`} className="text-accent font-medium text-[0.82rem] no-underline hover:underline">
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Invoices card */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between p-5 pb-0">
            <SectionTitle style={{ marginBottom: invoices.length > 0 ? undefined : 0 }}>Invoices ({invoices.length})</SectionTitle>
            <ButtonLink to={`/invoices/new?client_id=${client.id}`} size="sm">+ New Invoice</ButtonLink>
          </div>

          {invoices.length === 0 ? (
            <div className="text-center py-8 px-4 text-text-3 p-5">
              <Receipt size={36} strokeWidth={1.25} className="text-text-4 mb-2 mx-auto" />
              <div className="font-semibold text-text-heading mb-[0.3rem]">No invoices yet</div>
              <div className="text-sm mb-3">Create an invoice for this client</div>
              <ButtonLink to={`/invoices/new?client_id=${client.id}`} size="sm">+ New Invoice</ButtonLink>
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className={thClass}>Invoice #</th>
                    <th className={thClass}>Patient</th>
                    <th className={thClass}>Date</th>
                    <th className={`${thClass} text-right`}>Amount</th>
                    <th className={thClass}>Status</th>
                    <th className={`${thClass} text-right`}></th>
                  </tr>
                </thead>
                <tbody className={tbodyHoverClass}>
                  {invoices.map(inv => {
                    const { color, bg } = STATUS_COLORS[inv.status as InvoiceStatus]
                    return (
                      <tr key={inv.id}>
                        <td className={`${tdClass} font-semibold font-mono text-[0.85rem]`}>
                          <Link to={`/invoices/${inv.id}`} className={crumbLinkClass}>
                            {inv.invoice_number}
                          </Link>
                        </td>
                        <td className={`${tdClass} text-text-2`}>
                          {inv.patient_name ? (
                            <Link to={`/patients/${inv.patient_id}`} className="text-text-2 no-underline hover:underline">
                              {inv.patient_name}
                            </Link>
                          ) : <span className="text-text-4">—</span>}
                        </td>
                        <td className={`${tdClass} text-text-2 whitespace-nowrap`}>
                          {new Date(inv.issued_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                        <td className={`${tdClass} text-right font-semibold`}>
                          R {parseFloat(inv.total_amount).toFixed(2)}
                        </td>
                        <td className={tdClass}>
                          <span
                            className="inline-flex items-center px-3 py-[0.2rem] rounded-full text-[0.82rem] font-semibold"
                            style={{ color, background: bg }}
                          >
                            {INVOICE_STATUS_LABELS[inv.status as InvoiceStatus]}
                          </span>
                        </td>
                        <td className={`${tdClass} text-right`}>
                          <Link to={`/invoices/${inv.id}`} className="text-accent font-medium text-[0.82rem] no-underline hover:underline">
                            View →
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
